import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Ruler, Sparkles, RotateCcw } from "lucide-react";

export const ProductInformation = ({ sizing, care, className }) => {
  const [activeTab, setActiveTab] = useState("sizing");
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0, opacity: 0 });
  const tabsListRef = useRef(null);
  const tabRefs = useRef({});

  useEffect(() => {
    const updateSlider = () => {
      const listEl = tabsListRef.current;
      const activeEl = tabRefs.current[activeTab];
      if (!listEl || !activeEl) return;

      const listRect = listEl.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();

      setSliderStyle({
        left: activeRect.left - listRect.left,
        width: activeRect.width,
        opacity: 1
      });
    };

    const rafId = window.requestAnimationFrame(updateSlider);
    window.addEventListener("resize", updateSlider);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", updateSlider);
    };
  }, [activeTab]);

  const assignTabRef = (value) => (node) => {
    if (node) {
      tabRefs.current[value] = node;
    }
  };

  return (
    <div className={cn("rounded-xl border border-border overflow-hidden", className)}>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList
          ref={tabsListRef}
          className="product-tabs-list w-full justify-start rounded-none bg-secondary/30 border-b border-border p-0 h-auto"
        >
          <span
            className="product-tab-slider"
            aria-hidden="true"
            style={{
              transform: `translateX(${sliderStyle.left}px)`,
              width: `${sliderStyle.width}px`,
              opacity: sliderStyle.opacity
            }}
          />
          <TabsTrigger 
            ref={assignTabRef("sizing")}
            value="sizing" 
            className="product-tab-trigger rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 px-4 gap-2"
          >
            <Ruler className="h-4 w-4" />
            Sizing & Fit
          </TabsTrigger>
          <TabsTrigger 
            ref={assignTabRef("care")}
            value="care" 
            className="product-tab-trigger rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 px-4 gap-2"
          >
            <Sparkles className="h-4 w-4" />
            Material & Care
          </TabsTrigger>
          <TabsTrigger 
            ref={assignTabRef("returns")}
            value="returns" 
            className="product-tab-trigger rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 px-4 gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Returns
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sizing" className="p-6 mt-0">
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-foreground mb-1">Fit</h4>
              <p className="text-sm text-muted-foreground">{sizing?.fit}</p>
              {sizing?.model && (
                <p className="text-sm text-muted-foreground mt-1">{sizing.model}</p>
              )}
              {sizing?.dimensions && (
                <p className="text-sm text-muted-foreground mt-1">Dimensions: {sizing.dimensions}</p>
              )}
            </div>

            {sizing?.guide && sizing.guide.length > 0 && (
              <div>
                <h4 className="font-medium text-foreground mb-3">Size Guide</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Size</th>
                        {sizing.guide[0].chest && <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Chest</th>}
                        {sizing.guide[0].length && <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Length</th>}
                        {sizing.guide[0].width && <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Width</th>}
                        {sizing.guide[0].height && <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Height</th>}
                        {sizing.guide[0].circumference && <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Circumference</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sizing.guide.map((row, index) => (
                        <tr key={index} className="border-b border-border/50 last:border-0">
                          <td className="py-2 pr-4 font-medium">{row.size}</td>
                          {row.chest && <td className="py-2 pr-4 text-muted-foreground">{row.chest}</td>}
                          {row.length && <td className="py-2 pr-4 text-muted-foreground">{row.length}</td>}
                          {row.width && <td className="py-2 pr-4 text-muted-foreground">{row.width}</td>}
                          {row.height && <td className="py-2 pr-4 text-muted-foreground">{row.height}</td>}
                          {row.circumference && <td className="py-2 pr-4 text-muted-foreground">{row.circumference}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="care" className="p-6 mt-0">
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-foreground mb-1">Material Feel</h4>
              <p className="text-sm text-muted-foreground">
                Natural wool with authentic texture. Each piece is unique due to the natural variations in the fiber.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-1">Care Summary</h4>
              <p className="text-sm text-muted-foreground">{care?.summary}</p>
            </div>
            {care?.instructions && (
              <div>
                <h4 className="font-medium text-foreground mb-2">Care Instructions</h4>
                <ul className="space-y-1">
                  {care.instructions.map((instruction, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-primary mt-1.5">•</span>
                      {instruction}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="returns" className="p-6 mt-0">
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-foreground mb-1">Return Policy</h4>
              <p className="text-sm text-muted-foreground">
                We want you to love your ROSKAM product. If you're not completely satisfied,
                you may return unworn items within 30 days of delivery for a full refund.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-2">Conditions</h4>
              <ul className="space-y-1">
                <li className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-primary mt-1.5">•</span>
                  Item must be unworn and in original condition
                </li>
                <li className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-primary mt-1.5">•</span>
                  All tags must be attached
                </li>
                <li className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-primary mt-1.5">•</span>
                  Return shipping is the customer’s responsibility unless the item is defective
                </li>
              </ul>
            </div>
            <a
              href="/#returns"
              className="inline-flex items-center text-sm font-medium text-foreground hover:underline"
            >
              View full returns policy →
            </a>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
